import React, { useEffect, useState } from 'react';
import Form, { useForm } from '../../molecules/Form';
import Button from '../../molecules/Button';
import DragableComponent from '../DraggableComponent/DragableComponent';
import { MappingHolder } from './CreateCompaign.styles';
import { useLocation, useNavigate } from 'react-router-dom';
import Toast from '../../molecules/Toast';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../Context/authContext';
import { normalize } from '../../../helpers/common';
const Step2 = ({ setData, data, setAddLeadsDataTab, setOpenLeadsModal, setActiveDataTab, isInLeadsStep }) => {
  const [form] = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [dragItems, setDragItems] = useState({ root: [], container1: [] });
  const { selectedWorkspace, hasPermission } = useContextHook(AuthContext, v => ({
    selectedWorkspace: v.selectedWorkspace,
    hasPermission: v.hasPermission,
  }));

  const [isLeadsStep, setIsLeadsStep] = useState(isInLeadsStep);
  const predefinedFields = [
    { key: 'first_name', label: 'First Name', editable: false, required: true },
    { key: 'last_name', label: 'Last Name', editable: false, required: true },
    { key: 'email', label: 'Email', editable: false, required: false },
    { key: 'company_name', label: 'Company Name', editable: false, required: false },
    { key: 'linkedin_url', label: 'LinkedIn URL', editable: false, required: false },
    { key: 'linkedin_id', label: 'LinkedIn ID', editable: false, required: false },
    { key: 'website', label: 'Website', editable: false, required: false },
    { key: 'person_city', label: 'Person City', editable: false, required: false },
    { key: 'person_country', label: 'Person Country', editable: false, required: false },
    { key: 'title', label: 'Title', editable: false, required: false },
    { key: 'industry', label: 'Industry', editable: false, required: false },
    { key: 'phone_number', label: 'Phone Number', editable: false, required: false },
    { key: 'profile_photo', label: 'Profile Picture', editable: false, required: false },
    { key: 'job_description', label: 'Job Description', editable: false, required: false },
    { key: 'company_description', label: 'Company Description', editable: false, required: false },
  ];

  const [dragableData, setDragableData] = useState(predefinedFields);

  const handleAddField = () => {
    setDragableData(prev => {
      const updated = [...prev, { key: 'newKey', label: 'New Field', editable: true, required: false }];

      setDragItems(prevItems => ({
        ...prevItems,
        root: [...prevItems.root, null],
      }));

      return updated;
    });
  };

  const handleEditLabel = (index, newLabel) => {
    setDragableData(prev => {
      const updated = [...prev];
      updated[index].label = newLabel;
      updated[index].key = newLabel.toLowerCase().replace(/\s+/g, '_');
      return updated;
    });
  };



  const handelSubmit = async () => {
    const emailFieldIndex = dragableData.findIndex(item => item.key === 'email');
    if (emailFieldIndex === -1 || !dragItems.root?.[emailFieldIndex]?.rowKey) {
      Toast({ type: 'error', message: 'Please map the email column before proceeding' });
      return;
    }

    const mappedEmailColumn = dragItems.root?.[emailFieldIndex]?.rowKey;

    const emailRegex =
      /^[A-Z0-9]+([._%+-][A-Z0-9]+)*@[A-Z0-9-]+(\.[A-Z0-9-]+)*\.[A-Z]{2,}$/i;

    const validRows = data?.step1?.rows?.filter(row => {
      let emailValue = null;
      if (mappedEmailColumn && mappedEmailColumn in row) {
        emailValue = row[mappedEmailColumn];
      }

      if (!emailValue || typeof emailValue !== 'string') {
        return false;
      }

      const trimmedEmail = emailValue.trim().toLowerCase();

      return trimmedEmail.length > 0 && emailRegex.test(trimmedEmail);
    });

    if (!validRows || validRows.length === 0) {
      Toast({
        type: 'error',
        message: 'No rows found with valid emails in the mapped email column. Please check your mapping.',
      });
      return;
    }

    // Map only the valid rows
    const mappedRows = validRows.map(row => {
      const mappedRow = {};
      const customFields = {};

      dragableData.forEach((item, index) => {
        const mappedItem = dragItems.root?.[index];
        const csvColumnName = mappedItem?.rowKey;

        let cellValue = null;
        if (csvColumnName && csvColumnName in row) {
          cellValue = row[csvColumnName];
        }

        const isPredefinedField = predefinedFields.some(predefined => predefined.key === item.key);

        if (isPredefinedField) {
          mappedRow[item.key] = cellValue;
        } else {
          customFields[item.key] = cellValue;
        }
      });

      mappedRow.custom_fields = customFields;
      return mappedRow;
    });

    const columnNames = dragableData
      .map((item, index) => {
        const mappedColumn = dragItems.root?.[index]?.rowKey;
        if (mappedColumn) {
          return item?.key;
        }
        return null;
      })
      .filter(Boolean);

    const col_map_analysis = Object.fromEntries(
      dragableData.map((item, index) => {
        const mappedItem = dragItems.root?.[index];
        const value = mappedItem?.rowKey || '';
        const label = item?.label || '';
        return [label, value];
      }),
    );

    setData(prev => ({
      ...prev,
      mappedData: {
        leadList: mappedRows,
        columnNames: columnNames,
        col_map_analysis: col_map_analysis,
        workspaceId: selectedWorkspace.id,
      },
      isHoudinyLeads: false,
    }));

    // Navigate to LeadsData tab
    if (isLeadsStep) {
      setAddLeadsDataTab('leadsData');
    } else {
      setActiveDataTab('leadsData');
    }
  };

  useEffect(() => {
    setIsLeadsStep(isInLeadsStep);
  }, [isInLeadsStep]);

  return (
    <>
      <MappingHolder>
        <div className="col-maping">
          <div className="head">
            <strong className="heading">Map your CSV columns</strong>
            <p>
              Drag and drop CSV columns to map them with the required fields. Fields marked as required must be mapped.
            </p>
          </div>
          <Form form={form} onSubmit={handelSubmit}>
            <div className="col-wrap">
              <DragableComponent
                initialItems={{
                  root: Array(dragableData.length).fill(null),
                  container1:
                    data?.step1?.columnNames
                      ?.filter(name => name && name.trim() !== '')
                      ?.map((name, index) => ({
                        id: `step2-col-${index}`,
                        title: name,
                        rowKey: `${normalize(name)}_${index}`,
                        originalIndex: index,
                      })) || [],
                }}
                onItemsChange={setDragItems}
                dragableData={dragableData}
                onEditLabel={handleEditLabel}
                onAddField={handleAddField}
              />
            </div>
            <div className="btn-holder">
              <div className="btn-wrap">
                {/* <Button variant="outline" width="155px" onClick={goBack}>
                Back
              </Button> */}
                <Button
                  newStyle
                  variant="outline"
                  width="155px"
                  rounded
                  type="button"
                  onClick={() => {
                    if (isLeadsStep) {
                      setAddLeadsDataTab('leads');
                    } else {
                      setOpenLeadsModal(false);
                    }
                  }}>
                  Back
                </Button>
              </div>
              <Button newStyle rounded variant="darkBlue" type="submit" width="155px">
                Next
              </Button>
            </div>
          </Form>
        </div>
      </MappingHolder>
    </>
  );
};

export default Step2;
